import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DomainAllowlistApiActions, DomainAllowlistUiActions } from '../actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';
import { DomainAllowlistFacade } from '../../facade/domain-allowlist.facade';
import { DomainAllowlistService } from '../../service/domain-allowlist.service';
import { IDomainAllowlist } from '../../models/domain-allowlist';

@Injectable()
export class DomainAllowlistEffects {
  private actions$ = inject(Actions);
  private toastService = inject(ToastService);
  private service = inject(DomainAllowlistService);
  private facade = inject(DomainAllowlistFacade);

  constructor() {}

  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainAllowlistUiActions.GetDomainAllowlist),
      mergeMap(() =>
        this.service.getDomainAllowlist().pipe(
          map((items) =>
            DomainAllowlistApiActions.getDomainAllowlistSuccess({
              payload: Array.isArray(items) ? items : [items],
            })
          ),
          catchError((error) => of(DomainAllowlistApiActions.getDomainAllowlistError({ errors: error })))
        )
      )
    )
  );

  post$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainAllowlistUiActions.PostDomainAllowlist),
      mergeMap(({ item }) =>
        this.service.postDomainAllowlist(item).pipe(
          tap(() => this.toastService.show('Domain added successfully', 'success')),
          map(() => DomainAllowlistApiActions.postDomainAllowlistSuccess({ payload: item })),
          catchError((error) => {
            this.toastService.show(`Error adding domain: ${error.message || 'Unexpected error'}`, 'error');
            return of(DomainAllowlistApiActions.postDomainAllowlistError({ errors: error }));
          })
        )
      )
    )
  );

  patch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainAllowlistUiActions.PatchDomainAllowlist),
      mergeMap(({ id, changes }) =>
        this.service.patchDomainAllowlist(id, changes).pipe(
          tap(() => this.toastService.show('Domain updated successfully', 'success')),
          map((payload) =>
            DomainAllowlistApiActions.patchDomainAllowlistSuccess({ payload: payload as IDomainAllowlist })
          ),
          catchError((error) => {
            this.toastService.show(`Error updating domain: ${error.message || 'Unexpected error'}`, 'error');
            return of(DomainAllowlistApiActions.patchDomainAllowlistError({ errors: error }));
          })
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DomainAllowlistUiActions.DeleteDomainAllowlist),
      mergeMap(({ id }) =>
        this.service.deleteDomainAllowlist(id).pipe(
          tap(() => this.toastService.show('Domain deleted successfully', 'success')),
          map(() => DomainAllowlistApiActions.deleteDomainAllowlistSuccess({ id })),
          catchError((error) => {
            this.toastService.show(`Error deleting domain: ${error.message || 'Unexpected error'}`, 'error');
            return of(DomainAllowlistApiActions.deleteDomainAllowlistError({ errors: error }));
          })
        )
      )
    )
  );

  // refresh list after write ops (if you prefer refetch over optimistic updates)
  onWriteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DomainAllowlistApiActions.postDomainAllowlistSuccess,
          DomainAllowlistApiActions.patchDomainAllowlistSuccess,
          DomainAllowlistApiActions.deleteDomainAllowlistSuccess
        ),
        tap(() => this.facade.getDomainAllowlist())
      ),
    { dispatch: false }
  );
}
